import { useMemo } from "react";
import { motion } from "framer-motion";
import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import CommentIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import IosShareIcon from "@mui/icons-material/IosShare";

import { convertToShamsiRelative } from "../utils/dateUtils";
import { shareToInstagramStory } from "../utils/instagramStoryShare";
import { renderTextWithNewlines } from "../utils/textUtils";
import { formatNumber } from "../utils/formatNumber";
import AuthModal from "./AuthModal";
import ErrorToast from "./ErrorToast";
import SuccessToast from "./SuccessToast";
import { useOptimisticLike } from "../hooks/useLikes";
import { useCreateComment, useDeleteComment } from "../hooks/useComments";
import { useDeletePost } from "../hooks/usePosts";
import { useAuth } from "../lib/auth";
import { getApiUrl } from "../lib/api";
import VerificationBadge from "./VerificationBadge";
import { usePostNavigation } from "../hooks/usePostNavigation";
import { useTranslation } from "../hooks/useTranslation";
import LikesModal from "./LikesModal";
import CommentSection from "./CommentSection";

type Tag = {
  id: number;
  postId: number;
  name: string;
};

type PostProps = {
  id: string;
  hashCode?: string; // Add hash code for SEO-friendly URLs
  img: string;
  video?: string | null;
  caption: string;
  like: [],
  userId: number;
  postType?: 'regular' | 'poll';
  isHashBased?: boolean; // Add prop to indicate if post is loaded via hash
  isReelsView?: boolean; // New prop for Reels layout
  isLikedByCurrentUser?: boolean; // From main posts API when authenticated
  user?: {
    id: number;
    username: string;
    name: string;
    image: string;
    verified?: boolean;
  };
  comments?: any[];
  tags?: Tag[];
  createdAt?: string; // Add creation time
  showAllComments?: boolean; // New prop to control comment display
  poll?: {
    id: number;
    question: string;
    options: Array<{
      id: number;
      optionText: string;
      voteCount: number;
      percentage: number;
    }>;
    totalVotes: number;
    userVote?: number | null;
  };
};

const Post: React.FC<PostProps> = ({
  id,
  hashCode,
  caption,
  isReelsView = false,
  user,
  like = [],
  comments = [],
  createdAt,
  showAllComments = false,
  isLikedByCurrentUser: isLikedByCurrentUserProp,
}) => {
  // Early validation - don't render if post ID is invalid
  if (!id || id === 'null' || id === 'undefined') {
    return null;
  }

  /** SEO: canonical URL for this post so Google can index and show in results */
  const postPath = hashCode ? `/p/${hashCode}` : `/post/${id}`;



  const { t } = useTranslation();
  const { isAuthenticated, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { navigateToPostById, isPostPage } = usePostNavigation();

  const [currentPostId, setCurrentPostId] = useState(id);
  const [comment, setComment] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authAction, setAuthAction] = useState<"like" | "comment" | "vote">("like");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [visiableComment, setVisiableComment] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState<"post" | "comment">("post");
  const [deleteTargetId, setDeleteTargetId] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [shareStoryLoading, setShareStoryLoading] = useState(false);

  // Use comments from props (which come from React Query cache)
  const displayComments = useMemo(() => {
    if (!comments) return [];
    if (!Array.isArray(comments)) return [];
  
    return comments.filter(
      (c) =>
        c &&
        c.id &&
        typeof c.content === "string" &&
        c.user &&
        (c.user.username || c.user.name)
    );
  }, [comments]);

  useEffect(() => {
    setCurrentPostId(id);
  }, [id]);

  // Likes come from the main posts API – no per-post likes request
  const likes = Array.isArray(like) ? like : [];

  // Use API flag when present, otherwise derive from likes array and current user
  const hasLikes = useMemo(() => {
    if (isLikedByCurrentUserProp === true) return true;
    if (!isAuthenticated || !currentUser || !likes.length) return false;
    return likes.some((l: any) => {
      const likeUserId = l.userId ?? l.user_id ?? l.user?.id;
      return likeUserId != null && Number(likeUserId) === Number(currentUser.id);
    });
  }, [isAuthenticated, currentUser, likes, isLikedByCurrentUserProp]);

  const {
    displayLiked,
    displayCount,
    likePost: toggleLike,
    isPending: isLikePending,
  } = useOptimisticLike(
    String(id),
    currentUser?.id,
    likes.length,
    hasLikes
  );
  const createCommentMutation = useCreateComment();
  const deleteCommentMutation = useDeleteComment();
  const deletePostMutation = useDeletePost();

  const sendComment = async (e: any) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setAuthAction("comment");
      setShowAuthModal(true);
      return;
    }

    if (comment.trim()) {
      const commentContent = comment.trim();
      setComment(""); // Clear input immediately for better UX

      try {
        // Send comment to server - optimistic updates are handled in the mutation
        await createCommentMutation.mutateAsync({
          post_id: String(currentPostId),
          content: commentContent,
          user_id: Number(currentUser?.id)
        });
      } catch (error: any) {
        
        // Restore comment text on error
        setComment(commentContent);
        
        // Show error message
        setErrorMessage(error.message || 'خطا در ارسال کامنت');
        setShowError(true);
      }
    }
  };

  const likePost = async () => {
    if (!isAuthenticated) {
      setAuthAction("like");
      setShowAuthModal(true);
      return;
    }
    if (!id || id === "null" || id === "undefined") {
      setErrorMessage("خطا در شناسایی پست");
      setShowError(true);
      return;
    }
    try {
      await toggleLike();
    } catch {
      setErrorMessage("خطا در لایک کردن");
      setShowError(true);
    }
  };



  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const closeErrorToast = () => {
    setShowError(false);
    setErrorMessage("");
  };

  const closeSuccessToast = () => {
    setShowSuccess(false);
    setSuccessMessage("");
  };

  const handleDeletePost = () => {
    setDeleteType("post");
    setDeleteTargetId(String(id));
    setShowDeleteConfirm(true);
  };

  const handleVisableComment = () => {
    setVisiableComment(!visiableComment)
  }

  const handleShareToStory = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (shareStoryLoading) return;
    setShareStoryLoading(true);
    try {
      const base = window.location.origin;
      const postUrl = hashCode ? `${base}/p/${hashCode}` : `${base}/post/${id}`;
      let profilePictureUrl = user?.image
        ? user.image.startsWith("http")
          ? user.image
          : `${getApiUrl()}${user.image.replace(/^\//, "/")}`
        : undefined;
      // Use backend proxy for cross-origin profile images so canvas can load them (avoids CORS)
      if (profilePictureUrl && typeof window !== "undefined") {
        try {
          const urlOrigin = new URL(profilePictureUrl).origin;
          if (urlOrigin !== window.location.origin) {
            const apiBase = (getApiUrl() || "").replace(/\/$/, "");
            profilePictureUrl = `${apiBase}/api/proxy-image?url=${encodeURIComponent(profilePictureUrl)}`;
          }
        } catch {
          // keep original if URL parsing fails
        }
      }
      const result = await shareToInstagramStory({
        text: caption || "",
        authorName: user?.name || user?.username,
        profilePictureUrl,
        isVerified: user?.verified,
        url: postUrl,
      });
      if (result.fallbackDownload) {
        setSuccessMessage("تصویر ذخیره شد. آن را در اینستاگرام استوری به اشتراک بگذارید.");
        setShowSuccess(true);
      }
      if (result.error) {
        setErrorMessage(result.error);
        setShowError(true);
      }
    } finally {
      setShareStoryLoading(false);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setDeleteType("comment");
    setDeleteTargetId(commentId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteType === "post") {
        await deletePostMutation.mutateAsync(deleteTargetId);
        setSuccessMessage("پست با موفقیت حذف شد");
        setShowSuccess(true);
        // Only navigate if we're on a single post page, otherwise stay on current page
        if (isPostPage()) {
          setTimeout(() => navigate('/'), 1000); // Delay navigation to show success message
        }
      } else {
        await deleteCommentMutation.mutateAsync(deleteTargetId);
        setSuccessMessage("کامنت با موفقیت حذف شد");
        setShowSuccess(true);
      }
      setShowDeleteConfirm(false);
    } catch (error: any) {
      setErrorMessage(error.message || 'خطا در حذف');
      setShowError(true);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteType("post");
    setDeleteTargetId("");
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <>
      <ErrorToast
        message={errorMessage}
        isVisible={showError}
        onClose={closeErrorToast}
      />
      <SuccessToast
        message={successMessage}
        isVisible={showSuccess}
        onClose={closeSuccessToast}
      />
      <motion.article
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.2 }}
        className={isReelsView 
          ? "bg-transparent h-full flex flex-col relative" 
          : "bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 transition-colors duration-150"
        }
        aria-label={caption?.trim().slice(0, 80) || "پست متنی ورزشی"}
      >
        {/* Post Header */}
        {user && (
          <div className="flex items-start justify-between px-3 pt-3 pb-2">
            <div 
              className="flex items-center flex-1 group cursor-pointer -me-2 py-2 rounded-full transition-colors duration-200"
              onClick={() => navigate(`/${user.username || user.id}`)}
            >
              {user.image ? (
                <img
                  src={user.image}
                  alt={user.name || user.username}
                  className="w-10 h-10 rounded-full object-cover me-3 ring-2 ring-transparent group-hover:ring-gray-200 dark:group-hover:ring-gray-700 transition-all"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center me-3 ring-2 ring-transparent group-hover:ring-gray-200 dark:group-hover:ring-gray-700 transition-all">
                  <span className="text-white text-base font-semibold">
                    {(user.name || user.username)?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-gray-900 dark:text-white group-hover:underline truncate">
                    {user.name || user.username}
                  </span>
                  <VerificationBadge verified={user.verified} size="sm" />
                  {createdAt && (
                    <Link
                      to={postPath}
                      className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap hover:underline hover:text-gray-700 dark:hover:text-gray-300"
                      aria-label="مشاهده پست"
                    >
                      · {convertToShamsiRelative(createdAt)}
                    </Link>
                  )}
                </div>
              </div>
            </div>
            
            {/* Post options dropdown - only show for post owner */}
            {isAuthenticated && currentUser && user && currentUser.username === user.username && (
              <div className="relative" ref={dropdownRef}>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-full transition-colors text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                  title="گزینه‌ها"
                  aria-label="گزینه‌های پست"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </motion.button>
                
                {/* Dropdown Menu */}
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden"
                    >
                      <motion.button
                        whileHover={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
                        onClick={() => {
                          setShowDropdown(false);
                          handleDeletePost();
                        }}
                        disabled={deletePostMutation.isPending}
                        className="w-full px-4 py-3 text-right text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-start gap-0 transition-colors"
                      >
                        {deletePostMutation.isPending ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            در حال حذف...
                          </>
                        ) : (
                          <>
                            <DeleteOutlineIcon className="w-4 h-4" />
                            حذف پست
                          </>
                        )}
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
        
        {/* Post Content */}
        <div className="px-4 py-2">
          <p className="text-start text-[15px] text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap break-words">
            {renderTextWithNewlines(caption)}
          </p>         
        </div>

        <div className="flex items-center justify-between px-2 py-1 mt-1">
          <div className="flex items-center gap-1">
            <div 
              className={`flex items-center gap-0.5 px-2.5 py-1.5 rounded-full transition-colors duration-200 touch-manipulation ${
                displayLiked
                  ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  : "text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400"
              }`}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={likePost}
                disabled={isLikePending}
                className="flex items-center justify-center p-0 border-0 bg-transparent disabled:opacity-100 disabled:cursor-default"
                aria-label={displayLiked ? "حذف لایک" : "لایک"}
              >
                {displayLiked ? (
                  <FavoriteIcon className="w-5 h-5" />
                ) : (
                  <FavoriteBorderIcon className="w-5 h-5" />
                )}
              </motion.button>
              {displayCount > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLikesModal(true);
                  }}
                  className={`text-[13px] font-medium cursor-pointer select-none p-0 border-0 bg-transparent ${
                    displayLiked ? "text-red-500" : "text-gray-500 dark:text-gray-400"
                  }`}
                  aria-label="مشاهده لیست لایک‌ها"
                >
                  ({formatNumber(displayCount)})
                </motion.button>
              )}
            </div>

            {/* Comment Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleVisableComment}
              className="group flex items-center gap-0 px-3 py-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200"
              aria-label="نظر"
            >
              <CommentIcon className="w-5 h-5" />
              {displayComments.length > 0 && (
                <span className="text-[13px] font-medium min-w-[20px] text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400">
                  {formatNumber(displayComments.length)}
                </span>
              )}
            </motion.button>

            {/* Share to Instagram Story */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleShareToStory}
              disabled={shareStoryLoading}
              className="group flex items-center gap-0 px-3 mt-[-5px] py-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200 disabled:opacity-60"
              aria-label="اشتراک‌گذاری در استوری اینستاگرام"
            >
              <IosShareIcon className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
     
  

        {/* Comments Section */}
        <CommentSection
          comments={displayComments}
          showAllComments={showAllComments}
          initialVisibleCount={3}
          currentUserId={currentUser?.id}
          isAuthenticated={isAuthenticated}
          deleteCommentMutation={deleteCommentMutation}
          deleteTargetId={deleteTargetId}
          onDeleteComment={handleDeleteComment}
          onViewMoreClick={
            hashCode
              ? () => navigateToPostById(id, hashCode)
              : () => navigate(`/post/${id}`)
          }
        />
        {/* Comment Input */}
        {visiableComment && (
          <form 
            onSubmit={sendComment}
            className="flex items-center gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-800"
          >
            {currentUser?.image ? (
              <img
                src={currentUser.image}
                alt={currentUser.name || currentUser.username || 'User'}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-1 ring-gray-200 dark:ring-gray-700"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 ring-1 ring-gray-200 dark:ring-gray-700">
                <span className="text-[13px] text-white text-sm font-semibold">
                  {(currentUser?.name || currentUser?.username)?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-2 flex-1 bg-gray-100 dark:bg-gray-800 rounded-full px-2 py-1 focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 transition-all">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('post.addComment')}
                className="flex-1 bg-transparent border-none text-[10px] text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0"
                disabled={createCommentMutation.isPending}
              />
              {createCommentMutation.isPending ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 animate-spin text-blue-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={!comment.trim()}
                  className={`p-1.5 rounded-full transition-colors ${
                    comment.trim()
                      ? "bg-blue-500 hover:bg-blue-600 text-white"
                      : "bg-gray-300 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  }`}
                  aria-label={t('post.sendComment')}
                >
                  <SendIcon className="w-4 h-4 rtl:scale-x-[-1]" />
                </motion.button>
              )}
            </div>
          </form>
        )}
      </motion.article>

      {/* Modals */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        action={authAction}
      />

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={cancelDelete}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                  <DeleteOutlineIcon className="h-7 w-7 text-red-600 dark:text-red-400" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {deleteType === "post" ? "حذف پست" : "حذف کامنت"}
                </h3>
                
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {deleteType === "post" 
                    ? "آیا مطمئن هستید که می‌خواهید این پست را حذف کنید؟ این عمل قابل بازگشت نیست."
                    : "آیا مطمئن هستید که می‌خواهید این کامنت را حذف کنید؟ این عمل قابل بازگشت نیست."
                  }
                </p>
                
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={cancelDelete}
                    disabled={deletePostMutation.isPending || deleteCommentMutation.isPending}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    انصراف
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDelete}
                    disabled={deletePostMutation.isPending || deleteCommentMutation.isPending}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {(deletePostMutation.isPending || deleteCommentMutation.isPending) ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        در حال حذف...
                      </>
                    ) : (
                      "حذف"
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <LikesModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        likes={likes}
      />
    </>
  );
};
export default Post;
